import { makeAutoObservable, runInAction } from 'mobx';

import {
  fetchAuthSession,
  fetchUserAttributes,
  signOut,
} from 'aws-amplify/auth';

import Router from 'next/router';
import { setPortalAccessToken } from '@/utils/api';
import { APP_ROUTES } from '@/utils/routes';
import { User, UserEditableKey } from '@/types/auth';

const DEFAULT_USER: User = {
  isAuthenticated: false,
  isLoading: false,
  email: '',
  given_name: '',
  family_name: '',
};

export class AuthStore {
  constructor() {
    makeAutoObservable(this);
  }

  signUpEmail = '';

  resetPasswordEmail = '';

  isInitializing = true;

  user: User = { ...DEFAULT_USER };

  handleLogout = async () => {
    try {
      this.user = { ...DEFAULT_USER };

      await signOut({ global: true });
    } catch (error) {
      console.error(error);
    }
  };

  autoLogin = async () => {
    try {
      runInAction(() => {
        this.user.isLoading = true;
      });

      const attributes = await fetchUserAttributes();
      const { tokens } = await fetchAuthSession();
      if (tokens?.accessToken) {
        await this.handleLoginSuccess(
          attributes,
          tokens.accessToken.toString(),
          Router.asPath
        );
      }
    } catch (error) {
      console.error(error);
    } finally {
      runInAction(() => {
        this.user.isLoading = false;
        this.isInitializing = false;
      });
    }
  };

  setSignUpEmail = (email: string) => {
    this.signUpEmail = email;
  };

  setResetPasswordEmail = (email: string) => {
    this.resetPasswordEmail = email;
  };

  handleLoginSuccess = async (
    userAttributes: Omit<User, 'isAuthenticated' | 'isLoading'>,
    accessToken: string,
    redirectPath?: string
  ) => {
    try {
      setPortalAccessToken(accessToken);

      runInAction(() => {
        this.user = {
          isAuthenticated: true,
          isLoading: false,
          ...userAttributes,
        };
      });

      Router.push(redirectPath || APP_ROUTES.DASHBOARD);
    } catch (error) {
      console.error(error);
    }
  };

  updateUserAttribute = (name: UserEditableKey, value: string) => {
    this.user[name] = value;
  };

  refreshToken = async () => {
    try {
      const { tokens } = await fetchAuthSession({ forceRefresh: true });
      const accessToken = tokens?.accessToken.toString();

      if (accessToken) {
        setPortalAccessToken(accessToken);
      }

      return accessToken;
    } catch (error) {
      console.error('Error refreshing tokens', error);
    }
  };
}

const authStore = new AuthStore();

export default authStore;
